import {
    Button,
    ButtonRow,
    Card,
    SectionHeader,
    Table,
    TableBody,
    TableCell,
    TableCellExpanded,
    TableCellSelectRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TableRowExpanded,
} from '@ksyos/design-system';
import { uniq } from 'lodash';
import { Fragment, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { IAdminProcess } from '@common/interfaces/adminProcesses';
import type { IServiceWorkflowStep } from '@frontend/components/pages/services/workflowGraph/WorkflowGraph';
import { ManagerAuthorization, StepStatus } from '@common/enums';
import { sortByPerformedDatetime } from '@common/utils/dates';
import { getDisplayName } from '@common/utils/getDisplayName';
import { Modal } from '@frontend/components/elements/modals/Modal';
import { NullableFormattedDatetime } from '@frontend/components/elements/NullableFormattedDatetime';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';

interface FailedStepsProps {
    processId: string;
    processWorkflowSteps: IAdminProcess['processWorkflowSteps'];
    serviceWorkflowSteps: IServiceWorkflowStep[];
}

export function FailedSteps(props: FailedStepsProps) {
    const [selectedProcessWorkflowStepId, setSelectedProcessWorkflowStepId] = useState<string | null>(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [hasTriggeredRerun, setHasTriggeredRerun] = useState(false);

    const failedProcessWorkflowSteps = useMemo(
        () => props.processWorkflowSteps.filter((steps) => steps.status === StepStatus.failed),
        [props.processWorkflowSteps]
    );

    const failedServiceWorkflowStepIds = useMemo(
        () =>
            uniq(
                failedProcessWorkflowSteps.map(
                    (failedProcessWorkflowStep) => failedProcessWorkflowStep.serviceWorkflowStepId
                )
            ),
        failedProcessWorkflowSteps
    );

    if (!hasOneAuthorization(ManagerAuthorization.failedProcessWorkflowStepRerun)) {
        return null;
    }

    if (!failedProcessWorkflowSteps || !failedProcessWorkflowSteps.length) {
        return null;
    }

    return (
        <>
            <SectionHeader title={translateKey('processDetails.failedSteps')} />
            <Card>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader />
                                <TableHeader>{translateKey('processDetails.stepName')}</TableHeader>
                                <TableHeader>{translateKey('processDetails.performedOn')}</TableHeader>
                                <TableHeader>{translateKey('processDetails.performedByPractitioner')}</TableHeader>
                                <TableHeader>{translateKey('processDetails.performedByOrganization')}</TableHeader>
                                <TableHeader>Status</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {failedServiceWorkflowStepIds.map((failedServiceWorkflowStepId) => {
                                const processWorkflowSteps = props.processWorkflowSteps
                                    .filter((p) => p.serviceWorkflowStepId === failedServiceWorkflowStepId)
                                    .sort(sortByPerformedDatetime('desc'));

                                const processWorkflowStep = processWorkflowSteps.shift();
                                if (!processWorkflowStep) {
                                    return null;
                                }

                                return (
                                    <Fragment key={processWorkflowStep.id}>
                                        <TableRow>
                                            {processWorkflowStep.status === StepStatus.failed && !hasTriggeredRerun ? (
                                                <TableCellSelectRow
                                                    isSelected={
                                                        selectedProcessWorkflowStepId === processWorkflowStep.id
                                                    }
                                                    onSelect={() => handleSelect(processWorkflowStep.id)}
                                                />
                                            ) : (
                                                <TableCell />
                                            )}
                                            <TableCell>
                                                {getServiceWorkflowStepName(processWorkflowStep.serviceWorkflowStepId)}
                                            </TableCell>
                                            <TableCell>
                                                <NullableFormattedDatetime
                                                    value={processWorkflowStep.performedDatetime}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {processWorkflowStep.performerPractitioner
                                                    ? getDisplayName(processWorkflowStep.performerPractitioner)
                                                    : null}
                                            </TableCell>
                                            <TableCell>{processWorkflowStep.performerOrganization?.name}</TableCell>
                                            <TableCell>{processWorkflowStep.status}</TableCell>
                                        </TableRow>
                                        {processWorkflowSteps.map((p) => (
                                            <TableRowExpanded
                                                isExpanded={true}
                                                key={p.id}
                                            >
                                                <TableCellExpanded
                                                    isSelected={
                                                        selectedProcessWorkflowStepId === p.serviceWorkflowStepId
                                                    }
                                                />
                                                <TableCellExpanded>
                                                    {getServiceWorkflowStepName(p.serviceWorkflowStepId)}
                                                </TableCellExpanded>
                                                <TableCellExpanded>
                                                    <NullableFormattedDatetime value={p.performedDatetime} />
                                                </TableCellExpanded>
                                                <TableCellExpanded>
                                                    {p.performerPractitioner
                                                        ? getDisplayName(p.performerPractitioner)
                                                        : null}
                                                </TableCellExpanded>
                                                <TableCellExpanded>{p.performerOrganization?.name}</TableCellExpanded>
                                                <TableCellExpanded>{p.status}</TableCellExpanded>
                                            </TableRowExpanded>
                                        ))}
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Card.Footer>
                    <ButtonRow justify="right">
                        <Button
                            color="secondary"
                            isDisabled={!selectedProcessWorkflowStepId || hasTriggeredRerun}
                            onClick={() => setModalIsOpen(true)}
                            variant="outline"
                        >
                            {translateKey('processDetails.rerunStep')}
                        </Button>
                    </ButtonRow>
                </Card.Footer>
            </Card>

            {modalIsOpen && (
                <Modal
                    actionButtons={[
                        <Button
                            key={uuidv4()}
                            onClick={() => setModalIsOpen(false)}
                            variant="outline"
                        >
                            {translateKey('common.no')}
                        </Button>,
                        <Button
                            key={uuidv4()}
                            onClick={rerunStep}
                            variant="solid"
                        >
                            {translateKey('common.yes')}
                        </Button>,
                    ]}
                    isOpen={modalIsOpen}
                    onClose={() => setModalIsOpen(false)}
                    size="small"
                    title={translateKey('processDetails.rerunStep')}
                >
                    <p>{translateKey('processDetails.confirmationRerunStep')}</p>
                </Modal>
            )}
        </>
    );

    function getServiceWorkflowStepName(serviceWorkflowStepId: string) {
        return props.serviceWorkflowSteps.find(
            (serviceWorkflowStep) => serviceWorkflowStep.id === serviceWorkflowStepId
        )?.name;
    }

    function handleSelect(processWorkflowStepId: string) {
        if (selectedProcessWorkflowStepId === processWorkflowStepId) {
            return setSelectedProcessWorkflowStepId(null);
        }
        setSelectedProcessWorkflowStepId(processWorkflowStepId);
    }

    async function rerunStep() {
        await fetchJson(`/api/admin/processes/${props.processId}/rerun/${selectedProcessWorkflowStepId}`, 'POST');
        setHasTriggeredRerun(true);
        setSelectedProcessWorkflowStepId(null);
        setModalIsOpen(false);
    }
}
